'use client'
import AppIcon from "@/app/components/atomic/AppIcon"
import {getWPPageComments} from "@/app/endpoints/wpEndpoints"
import {IWPComment, IWPCommentsPayload} from "@/app/models/wp"
import * as Sentry from "@sentry/nextjs"
import Image from "next/image"
import {useState} from "react"
import {PageCommentForm} from "./PageCommentForm"
import {useTracking} from "@/app/contexts/tracking-context"
import {ArticleTrackingParams} from "@/app/utils/article-tracking"
import logoRM from "@/public/images/RM_logo_V2_mini.svg"

const getFormattedDate = (date: string) => {
    const dateOptions: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Europe/Paris'};
    const dateObj = new Date(date ?? '');
    return dateObj.toLocaleDateString('fr-FR', dateOptions);
}

function decodeHTMLEntities(text: string): string {
    return text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&mdash;/g, '—')
        .replace(/&ndash;/g, '–')
}

export default function PageCommentsSection({wpPageId, comments, articleParams}: {
    wpPageId: string,
    comments: IWPCommentsPayload,
    articleParams?: ArticleTrackingParams
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [commentsDisplayed, setCommentsDisplayed] = useState<IWPComment[]>(comments.comments)

    async function fetchAllComments() {
        setIsLoading(true)
        try {
            setCommentsDisplayed((await getWPPageComments(wpPageId)).comments)
        } catch (error) {
            Sentry.captureException(error)
        } finally {
            setIsLoading(false)
        }
    }

    const updateComment = (commentId: string, updates: Partial<IWPComment>) => {
        setCommentsDisplayed(prevComments =>
            prevComments.map(comment =>
                comment.id === commentId
                    ? {...comment, ...updates}
                    : comment
            )
        )
    }

    return <>
        {
            commentsDisplayed.map((comment) => (
                <div key={comment.id} className="flex flex-col">
                    <Comment comment={comment} onUpdateComment={updateComment} articleParams={articleParams} />
                </div>
            ))
        }

        {commentsDisplayed.length < comments.totalComments && (
            <button
                className="underline w-fit mx-auto p-2"
                onClick={() => fetchAllComments()}
                disabled={isLoading}
            >
                {isLoading
                    ? <><AppIcon type={"regular"} iconClassName={"fa-spinner fa-spin"} /> Chargement</>
                    : <>Charger plus <AppIcon type={"regular"} iconClassName={"fa-chevron-down"} /></>
                }
            </button>
        )}
        {commentsDisplayed.length >= comments.totalComments && comments.totalComments >= 5 && <PageCommentForm wpPageId={wpPageId} articleParams={articleParams} />}
    </>
}

export function Comment({comment, onUpdateComment, isRoot = true, articleParams}: {
    comment: IWPComment,
    onUpdateComment: (commentId: string, updates: Partial<IWPComment>) => void,
    isRoot?: boolean,
    articleParams?: ArticleTrackingParams
}) {
    const [isLikeLoading, setIsLikeLoading] = useState(false)
    const {track} = useTracking()

    const handleLikeToggle = async () => {
        if (isLikeLoading) return

        setIsLikeLoading(true)
        try {
            const response = await fetch('/api/comments/toggle-like', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    commentId: comment.id
                })
            })

            if (!response.ok) {
                throw new Error(`Toggle like failed with status ${response.status}`)
            }

            const result = await response.json()

            if (result.success) {
                onUpdateComment(comment.id, {
                    isLiked: result.isLiked,
                    totalLikes: result.totalLikes
                })

                track({event: 'like_commentaire', ...articleParams})
            }
        } catch (error) {
            Sentry.captureException(error)
        } finally {
            setIsLikeLoading(false)
        }
    }

    return (
        <div className=" bg-white rounded-2xl flex flex-col p-4 md:p-8">
            <div className="flex flex-col gap-4 w-full">
                <div className="flex w-full justify-between">
                    <div className="flex gap-1">
                        <div className="h-6 w-6 flex bg-pastel-orange text-white rounded-full items-center justify-center">
                            {comment.authorName?.[0]}
                        </div>

                        {comment.authorName}
                    </div>

                    <span className="font-light text-rm-12 text-rm-gray">
                        Le {getFormattedDate(comment.date)}
                    </span>
                </div>

                {comment.title &&
                    <h3 className="font-semibold text-rm-16">
                        {comment.title}
                    </h3>
                }

                <div
                    dangerouslySetInnerHTML={{__html: comment.commentContent}}
                    className="font-light"
                />
            </div>

            {comment.response &&
                <Answer comment={comment.response} onUpdateComment={onUpdateComment} articleParams={articleParams} />
            }

            {isRoot &&
                <div className="ml-auto">
                    <button
                        type="button"
                        onClick={handleLikeToggle}
                        disabled={isLikeLoading}
                        aria-pressed={comment.isLiked}
                        aria-label={comment.isLiked ? 'Retirer mon like' : 'Liker ce commentaire'}
                        className={`flex items-center justify-center rounded-[100px] px-4 py-1 text-rm-12 font-semibold
                                    hover:bg-blue-200 hover:text-rm-blue transition-all duration-300 select-none cursor-pointer group
                                    ${comment.isLiked ? 'bg-rm-blue text-white' : 'bg-blue-100 text-pale'}
                                    ${isLikeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {comment.totalLikes || 0}

                        <AppIcon
                            iconClassName={isLikeLoading ? "fa-spinner fa-spin" : "fa-thumbs-up"}
                            type="regular"
                            className={`text-rm-12 ml-rm-5 group-hover:text-rm-blue ${isLikeLoading ? '' : 'group-hover:animate-bounce'}
                                        ${comment.isLiked ? 'text-white' : 'text-pale'}`}
                        />
                    </button>
                </div>
            }
        </div>
    )
}

export function Answer({comment, onUpdateComment, articleParams}: {
    comment: IWPComment,
    onUpdateComment: (commentId: string, updates: Partial<IWPComment>) => void,
    articleParams?: ArticleTrackingParams
}) {
    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="h-[1px] w-full max-w-[200px] md:max-w-xs bg-rm-gray mx-auto mt-6 mb-2" />

            <div className="flex w-full justify-between">
                <div className="flex gap-1">
                    <div className="h-6 w-6 flex bg-rm-blue/20 rounded-full items-center justify-center">
                        <Image src={logoRM} alt={`Reassurez-moi`} width={16} height={16} />
                    </div>

                    Expert Réassurez-moi
                </div>

                <span className="font-light text-rm-12 text-rm-gray">
                    Le {getFormattedDate(comment.date)}
                </span>
            </div>

            <div
                dangerouslySetInnerHTML={{__html: decodeHTMLEntities(comment.commentContent)}}
                className="font-light"
            />

            {comment.response &&
                <Comment comment={comment.response} onUpdateComment={onUpdateComment} isRoot={false} articleParams={articleParams} />
            }
        </div>
    )
}
